import { UploadStatus } from 'components/UploadStatus';
import type { IErrorHandlingRequestState } from 'features/errorHandling/errorHandlingSlice';
import { RequestStatus } from 'features/request/requestSlice';
import type { ValidationMessage } from 'gql/graphql';
import type { AnyObject } from 'types/AnyObject';
import type { ButtonPropView } from 'uikit';
import { Button, cnMixSpace } from 'uikit';

import type React from 'react';
import { useMemo } from 'react';

interface IErrorHandlingModalProps {
    titleText: string;
    isSubmitted: boolean;
    statusRequest: RequestStatus;
    errorsRequest: AnyObject[];
    handleCloseModalError: () => void;
    dataRequest?: IErrorHandlingRequestState | null;
    requestCode?: number;
    createReportBtnId?: string;
    isSuccessProcessed?: boolean;
    handleCreateReport?: () => void;
    customButtonText?: string;
    viewButton?: ButtonPropView;
    style?: React.CSSProperties;
}

const STOP_WORDS = ['current user is not authorized'];

export const ErrorHandlingModal: React.FC<IErrorHandlingModalProps> = (props) => {
    const {
        titleText,
        isSubmitted,
        statusRequest,
        dataRequest,
        errorsRequest,
        requestCode,
        handleCloseModalError,
        handleCreateReport,
        createReportBtnId,
        isSuccessProcessed,
        customButtonText,
        viewButton,
        style,
    } = props;

    const { result, validation, buttonText, message } = dataRequest || {};

    const btnText = useMemo(() => customButtonText || buttonText || 'Закрыть', [buttonText, customButtonText]);

    const stopWordsReceived = useMemo(() => {
        if (errorsRequest && Array.isArray(errorsRequest)) {
            return STOP_WORDS.some((err) => errorsRequest.some((error) => error.message.includes(err)));
        }

        return false;
    }, [errorsRequest]);

    const isNotShowModal = useMemo(() => requestCode === 500, [requestCode]);

    const parseErrorsData = useMemo(() => {
        if (requestCode && requestCode > 500) {
            return {
                message: `Ошибка запроса. ${requestCode} Service Unavailable`,
            };
        }

        if (Array.isArray(errorsRequest) && errorsRequest.length) {
            if (errorsRequest.length > 1) {
                return {
                    message: errorsRequest[0].message as string,
                    validation: errorsRequest.slice(1) as ValidationMessage[],
                    hideValidation: dataRequest?.hideValidation,
                };
            }

            return {
                message: errorsRequest[0].message as string,
            };
        }

        return undefined;
    }, [dataRequest?.hideValidation, errorsRequest, requestCode]);

    const uploadStatusContent = useMemo(() => {
        const withCreateReportBtn = validation?.length && !!handleCreateReport;
        const closeBtnViewStyle = withCreateReportBtn ? 'ghost' : 'primary';

        return (
            <>
                {withCreateReportBtn && (
                    <Button id={createReportBtnId} label="Сформировать отчет об ошибке" onClick={handleCreateReport} />
                )}
                <Button
                    className={cnMixSpace({
                        mL: 'xs',
                    })}
                    label={btnText}
                    view={viewButton || closeBtnViewStyle}
                    onClick={handleCloseModalError}
                />
            </>
        );
    }, [btnText, createReportBtnId, handleCloseModalError, handleCreateReport, validation?.length, viewButton]);

    return stopWordsReceived || isNotShowModal ? null : (
        <>
            {isSubmitted && statusRequest === RequestStatus.SUCCESS && result === false && (
                <UploadStatus
                    isOpened
                    handleCloseModal={handleCloseModalError}
                    data={dataRequest}
                    titleText={titleText}
                    status="error"
                    btnText={btnText}
                    style={style}
                >
                    {uploadStatusContent}
                </UploadStatus>
            )}
            {isSubmitted && parseErrorsData && statusRequest === RequestStatus.ERROR && (
                <UploadStatus
                    isOpened
                    handleCloseModal={handleCloseModalError}
                    data={parseErrorsData}
                    titleText={titleText}
                    status="error"
                    style={style}
                >
                    {uploadStatusContent}
                </UploadStatus>
            )}
            {isSuccessProcessed &&
                isSubmitted &&
                statusRequest === RequestStatus.SUCCESS &&
                result === true &&
                (message || validation?.length) && (
                    <UploadStatus
                        isOpened
                        handleCloseModal={handleCloseModalError}
                        data={dataRequest}
                        titleText={titleText}
                        status="success"
                        style={style}
                    >
                        {uploadStatusContent}
                    </UploadStatus>
                )}
        </>
    );
};

ErrorHandlingModal.displayName = 'ErrorHandlingModal';
